import { matchPath } from "react-router-dom";

export const ROOT_DOMAIN_HOST = "workscope.app";
export const ROOT_DOMAIN_WWW_HOST = "www.workscope.app";
export const DASHBOARD_DOMAIN_HOST = "dashboard.workscope.app";

export const ROUTE_PATHS = {
  landing: "/",
  dashboardHome: "/",
  pricing: "/pricing",
  affiliate: "/affiliate",
  login: "/login",
  createAccount: "/create-account",
  payment: "/payment",
  success: "/success",
  acceptInvite: "/accept-invite",
  terms: "/terms",
  privacy: "/privacy",
  smsConsent: "/sms-consent",
  smsConsentLegacy: "/legal/sms-consent",
  smsOptInProof: "/sms-opt-in-proof",
  approve: "/approve/:token",
  dashboardLegacy: "/dashboard",
  tickets: "/tickets",
  schedule: "/schedule",
  ticketNew: "/tickets/new",
  ticketDetail: "/tickets/:id",
  projects: "/projects",
  projectDetail: "/projects/:id",
  clients: "/clients",
  clientDetail: "/clients/:id",
  approvals: "/approvals",
  billing: "/billing",
  reports: "/reports",
  team: "/team",
  settings: "/settings",
  upgradePlan: "/upgrade-plan",
} as const;

const ROOT_DOMAIN_PATTERNS = [
  ROUTE_PATHS.pricing,
  ROUTE_PATHS.affiliate,
  ROUTE_PATHS.createAccount,
  ROUTE_PATHS.payment,
  ROUTE_PATHS.success,
  ROUTE_PATHS.acceptInvite,
  ROUTE_PATHS.terms,
  ROUTE_PATHS.privacy,
  ROUTE_PATHS.smsConsent,
  ROUTE_PATHS.smsConsentLegacy,
  ROUTE_PATHS.smsOptInProof,
  ROUTE_PATHS.approve,
] as const;

const DASHBOARD_DOMAIN_PATTERNS = [
  ROUTE_PATHS.login,
  ROUTE_PATHS.schedule,
  ROUTE_PATHS.tickets,
  ROUTE_PATHS.ticketNew,
  ROUTE_PATHS.ticketDetail,
  ROUTE_PATHS.projects,
  ROUTE_PATHS.projectDetail,
  ROUTE_PATHS.clients,
  ROUTE_PATHS.clientDetail,
  ROUTE_PATHS.approvals,
  ROUTE_PATHS.billing,
  ROUTE_PATHS.reports,
  ROUTE_PATHS.team,
  ROUTE_PATHS.settings,
  ROUTE_PATHS.upgradePlan,
] as const;

export function isRootDomainHost(hostname: string) {
  return hostname === ROOT_DOMAIN_HOST || hostname === ROOT_DOMAIN_WWW_HOST;
}

export function isDashboardDomainHost(hostname: string) {
  return hostname === DASHBOARD_DOMAIN_HOST;
}

export function isManagedDomainHost(hostname: string) {
  return isRootDomainHost(hostname) || isDashboardDomainHost(hostname);
}

export function isApprovalPath(pathname: string) {
  return matchPath({ path: ROUTE_PATHS.approve, end: true }, pathname) !== null;
}

export function isDashboardLegacyPath(pathname: string) {
  return matchPath({ path: ROUTE_PATHS.dashboardLegacy, end: true }, pathname) !== null;
}

function matchesAnyPattern(pathname: string, patterns: readonly string[]) {
  return patterns.some((pattern) => matchPath({ path: pattern, end: true }, pathname) !== null);
}

export function getRequiredDomainForPath(pathname: string): "root" | "dashboard" | null {
  if (isApprovalPath(pathname)) return "root";
  if (isDashboardLegacyPath(pathname)) return "dashboard";
  if (matchesAnyPattern(pathname, ROOT_DOMAIN_PATTERNS)) return "root";
  if (matchesAnyPattern(pathname, DASHBOARD_DOMAIN_PATTERNS)) return "dashboard";
  return null;
}

export function getCanonicalPathname(pathname: string) {
  if (isDashboardLegacyPath(pathname)) return ROUTE_PATHS.dashboardHome;
  return pathname;
}

function toAbsoluteUrl(origin: string, path: string) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${origin}${path}`;
}

export function getRootDomainUrl(path: string) {
  if (typeof window !== "undefined") {
    if (!isManagedDomainHost(window.location.hostname) || isRootDomainHost(window.location.hostname)) {
      return path;
    }
  }

  return toAbsoluteUrl(`https://${ROOT_DOMAIN_HOST}`, path);
}

export function getDashboardDomainUrl(path: string) {
  if (typeof window !== "undefined") {
    if (!isManagedDomainHost(window.location.hostname) || isDashboardDomainHost(window.location.hostname)) {
      return path;
    }
  }

  return toAbsoluteUrl(`https://${DASHBOARD_DOMAIN_HOST}`, path);
}
